import * as Sentry from '@sentry/react';
import mixpanel from 'mixpanel-browser';

const APP_HOSTS = ['app.sparklayer.io', 'app.dev.sparklayer.io'];
const MIXPANEL_TOKEN = 'ab795b7a2dc0a7dd5e60bc733715efb5';
const EXTENSION_URL = /(chrome|moz|safari(-web)?)-extension:\/\//;

// Extensions reject on our page with plain objects, which carry no frames for
// `thirdPartyErrorFilterIntegration` to check. 4900 is the EIP-1193 wallet disconnect code.
function isExtensionRejection(reason: unknown): boolean {
  if (typeof reason !== 'object' || reason === null || reason instanceof Error) {
    return false;
  }
  const { code, stack } = reason as { code?: unknown; stack?: unknown };
  return code === 4900 || (typeof stack === 'string' && EXTENSION_URL.test(stack));
}

export function initAnalytics(router: object) {
  if (!APP_HOSTS.includes(window.location.host)) {
    return;
  }

  Sentry.init({
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb?.data?.url?.includes('clarity.ms')) {
        return null;
      }
      return breadcrumb;
    },
    beforeSend(event, hint) {
      return isExtensionRejection(hint.originalException) ? null : event;
    },
    dsn: 'https://b2cd1b9778ed4f8abe25362246e2c35f@o932531.ingest.sentry.io/5881317',
    environment: window.location.host,
    ignoreErrors: [
      // Ignore network errors which are likely caused by user's unreliable network connection
      'error loading dynamically imported module',
      'Failed to fetch dynamically imported module',
      'Load failed',
      'NetworkError',
      'Network Error',
      'Unable to preload CSS',

      // https://stackoverflow.com/a/50387233
      'ResizeObserver loop limit exceeded',

      'ResizeObserver loop completed with undelivered notifications.',
      'Request failed with status code 401',
      'Request failed with status code 419',

      // Rejections from third-party scripts and extensions, nothing of ours rejects with these
      "You don't have permissions. Reference number",
      'Object captured as promise rejection with keys: [object has no keys]',
    ],
    integrations: [
      Sentry.tanstackRouterBrowserTracingIntegration(router),
      Sentry.thirdPartyErrorFilterIntegration({
        filterKeys: ['spark-dash'],
        behaviour: 'drop-error-if-exclusively-contains-third-party-frames',
      }),
    ],
    // __SENTRY_RELEASE__ is statically replaced at build time by Vite
    // biome-ignore lint/correctness/noUndeclaredVariables: defined in build process
    release: __SENTRY_RELEASE__,
    tracesSampleRate: 0.1,
    sendDefaultPii: true,
  });

  mixpanel.init(MIXPANEL_TOKEN, { track_pageview: false });
}
